<template>
  <div class="w-full p-4">
    <div class="flex flex-row justify-between items-center pb-2 border-b border-b-gray-400 mb-4">
      <h2 class="text-lg font-semibold">Submission Delegates</h2>
      <button
        class="px-3 py-1.5 text-sm rounded-md bg-accent-primary text-white hover:bg-accent-secondary"
        @click="openAdd"
      >+ New Delegation</button>
    </div>

    <div class="text-sm text-gray-600 mb-3">
      A delegation lets the <b>delegate</b> create and submit expense records on behalf of the
      <b>delegator</b>. The expense still routes through the delegator's manager and reimburses
      the delegator. The audit trail captures who actually did each action.
    </div>

    <div class="flex flex-row flex-wrap gap-3 mb-3 items-center text-sm">
      <input
        v-model="searchQuery"
        type="text"
        placeholder="Search..."
        class="p-1.5 border border-gray-400 rounded-md w-full sm:w-64"
      />
      <label class="flex items-center gap-1">
        <input type="checkbox" v-model="showInactive" />
        Include inactive history
      </label>
      <span class="ml-auto text-gray-500">{{ filtered.length }} row<span v-if="filtered.length !== 1">s</span></span>
    </div>

    <!-- Mobile card list -->
    <div class="md:hidden space-y-2">
      <div v-if="loading" class="p-4 text-center text-gray-500 text-sm">Loading...</div>
      <div v-else-if="filtered.length === 0" class="p-4 text-center text-gray-500 text-sm">
        No delegations to show.
      </div>
      <div
        v-for="d in filtered"
        :key="`m-${d.id}`"
        class="border border-gray-300 rounded-md bg-white p-3"
        :class="d.is_active ? '' : 'text-gray-400'"
      >
        <div class="flex items-start justify-between gap-2 mb-1">
          <div class="min-w-0">
            <div class="text-xs text-gray-500 uppercase tracking-wide">Delegator</div>
            <div class="text-sm font-semibold truncate">{{ fullName(d.delegator) }}</div>
            <div class="text-xs text-gray-500 truncate">{{ d.delegator.username }}</div>
          </div>
          <span
            v-if="d.is_active"
            class="px-2 py-0.5 text-xs rounded bg-green-100 text-green-700 flex-shrink-0"
          >active</span>
          <span v-else class="px-2 py-0.5 text-xs rounded bg-gray-200 text-gray-500 flex-shrink-0">inactive</span>
        </div>
        <div class="mt-2">
          <div class="text-xs text-gray-500 uppercase tracking-wide">Delegate (submits on their behalf)</div>
          <div class="text-sm truncate">{{ fullName(d.delegate) }}</div>
          <div class="text-xs text-gray-500 truncate">{{ d.delegate.username }}</div>
        </div>
        <div class="mt-2 text-xs text-gray-500">
          Since {{ formatDate(d.updated_at) }}
          · Set by {{ d.created_by ? fullName(d.created_by) : '—' }}
        </div>
        <div v-if="d.is_active && canEdit(d)" class="mt-3">
          <button
            class="text-xs px-2 py-1 border border-alert-primary text-alert-primary rounded hover:bg-alert-primary hover:text-white"
            @click="confirmDeactivate(d)"
          >Deactivate</button>
        </div>
      </div>
    </div>

    <!-- Desktop / tablet table -->
    <div class="hidden md:block border border-gray-400 rounded-md overflow-hidden">
      <table class="table-auto w-full text-sm">
        <thead class="bg-dark-primary text-white">
          <tr>
            <th class="text-left px-3 py-2">Delegator</th>
            <th class="text-left px-3 py-2">Delegate (submits for them)</th>
            <th class="text-left px-3 py-2">Status</th>
            <th class="text-left px-3 py-2">Since</th>
            <th class="text-left px-3 py-2">Set By</th>
            <th class="text-right px-3 py-2">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="loading">
            <td colspan="6" class="px-3 py-4 text-center text-gray-500">Loading...</td>
          </tr>
          <tr v-else-if="filtered.length === 0">
            <td colspan="6" class="px-3 py-4 text-center text-gray-500">
              No delegations to show.
            </td>
          </tr>
          <tr
            v-for="d in filtered"
            :key="d.id"
            class="border-t border-gray-200"
            :class="d.is_active ? '' : 'text-gray-400'"
          >
            <td class="px-3 py-2">
              {{ fullName(d.delegator) }}
              <div class="text-xs text-gray-500">{{ d.delegator.username }}</div>
            </td>
            <td class="px-3 py-2">
              {{ fullName(d.delegate) }}
              <div class="text-xs text-gray-500">{{ d.delegate.username }}</div>
            </td>
            <td class="px-3 py-2">
              <span v-if="d.is_active" class="px-2 py-0.5 text-xs rounded bg-green-100 text-green-700">active</span>
              <span v-else class="px-2 py-0.5 text-xs rounded bg-gray-200 text-gray-500">inactive</span>
            </td>
            <td class="px-3 py-2 text-xs">{{ formatDate(d.updated_at) }}</td>
            <td class="px-3 py-2 text-xs">
              {{ d.created_by ? fullName(d.created_by) : '—' }}
            </td>
            <td class="px-3 py-2 text-right">
              <button
                v-if="d.is_active && canEdit(d)"
                class="text-xs px-2 py-1 border border-alert-primary text-alert-primary rounded hover:bg-alert-primary hover:text-white"
                @click="confirmDeactivate(d)"
              >Deactivate</button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Add modal -->
    <div
      v-if="modalOpen"
      class="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
      @click.self="modalOpen = false"
    >
      <div class="bg-white rounded-md shadow-lg p-5 w-[30rem]">
        <h3 class="text-base font-semibold mb-3">New Delegation</h3>

        <label class="block text-sm mb-1">Delegator (whose expenses will be filed)</label>
        <select
          v-model="form.delegator_id"
          :disabled="!isAdmin"
          class="w-full p-1.5 border border-gray-400 rounded-md mb-3"
        >
          <option :value="null" disabled>Select delegator...</option>
          <option v-for="u in allUsers" :key="u.id" :value="u.id">{{ userLabel(u) }}</option>
        </select>
        <div v-if="!isAdmin" class="text-xs text-gray-500 -mt-2 mb-3">
          Non-admins can only grant a delegate from themselves.
        </div>

        <label class="block text-sm mb-1">Delegate (who gets to submit on their behalf)</label>
        <select
          v-model="form.delegate_id"
          class="w-full p-1.5 border border-gray-400 rounded-md mb-3"
        >
          <option :value="null" disabled>Select delegate...</option>
          <option
            v-for="u in allUsers"
            :key="u.id"
            :value="u.id"
            :disabled="u.id === form.delegator_id"
          >{{ userLabel(u) }}</option>
        </select>

        <div v-if="modalError" class="text-sm text-alert-primary mb-2">{{ modalError }}</div>

        <div class="flex justify-end gap-2">
          <button
            class="text-sm px-3 py-1.5 border border-gray-400 rounded hover:bg-gray-100"
            @click="modalOpen = false"
          >Cancel</button>
          <button
            class="text-sm px-3 py-1.5 rounded bg-accent-primary text-white hover:bg-accent-secondary disabled:opacity-50"
            :disabled="!form.delegator_id || !form.delegate_id || saving"
            @click="save"
          >{{ saving ? 'Saving...' : 'Create' }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default { name: "submission-delegates-admin" };
</script>

<script setup>
import { ref, computed, reactive, onMounted } from "vue";
import { useStore } from "vuex";
import { useToast } from "vue-toast-notification";
import {
  getDelegations, createDelegation, deactivateDelegation,
} from "@/services/Delegation.js";
import { getAllUsersWithGroups } from "@/services/User.js";

const store = useStore();
const $toast = useToast();

const loading = ref(false);
const delegations = ref([]);
const allUsers = ref([]);
const searchQuery = ref("");
const showInactive = ref(false);

const modalOpen = ref(false);
const saving = ref(false);
const modalError = ref("");
const form = reactive({ delegator_id: null, delegate_id: null });

const isAdmin = computed(() => {
  const p = store.state.auth.userPermissions || {};
  return !!(p.isDev || p.isSuperuser);
});

const currentUserId = computed(() => {
  return store.state.auth.user ? store.state.auth.user.id : null;
});

const filtered = computed(() => {
  const q = searchQuery.value.trim().toLowerCase();
  return delegations.value.filter((d) => {
    if (!showInactive.value && !d.is_active) return false;
    if (!q) return true;
    return [
      fullName(d.delegator),
      fullName(d.delegate),
      d.delegator.username,
      d.delegate.username,
    ].some((s) => (s || "").toLowerCase().includes(q));
  });
});

function fullName(u) {
  if (!u) return "—";
  const name = `${u.first_name || ""} ${u.last_name || ""}`.trim();
  return name || u.username;
}

function userLabel(u) {
  const name = `${u.first_name || ""} ${u.last_name || ""}`.trim();
  if (name && u.username) return `${name} <${u.username}>`;
  return name || u.username || "";
}

function formatDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString();
}

function canEdit(d) {
  return isAdmin.value || d.delegator.id === currentUserId.value;
}

async function load() {
  loading.value = true;
  const token = store.state.auth.token;
  try {
    const [delData, usersResp] = await Promise.all([
      getDelegations(token, "?page_size=500"),
      getAllUsersWithGroups(token),
    ]);
    delegations.value = Array.isArray(delData) ? delData : (delData.results || []);
    const users = usersResp && usersResp.data ? usersResp.data : [];
    allUsers.value = users
      .filter((u) => {
        const hasGroups = Array.isArray(u.groups) && u.groups.length > 0;
        const isTestUser = (u.username || "").startsWith("dev_test_exp_");
        return hasGroups || isTestUser;
      })
      .map((u) => ({ ...u }))
      .sort((a, b) => {
        const an = `${a.first_name || ""} ${a.last_name || ""}`.trim() || a.username;
        const bn = `${b.first_name || ""} ${b.last_name || ""}`.trim() || b.username;
        return an.localeCompare(bn);
      });
  } catch (error) {
    $toast.open({ message: "Failed to load delegations", type: "error", duration: 3000 });
  }
  loading.value = false;
}

function openAdd() {
  form.delegator_id = isAdmin.value ? null : currentUserId.value;
  form.delegate_id = null;
  modalError.value = "";
  modalOpen.value = true;
}

async function save() {
  if (!form.delegator_id || !form.delegate_id) return;
  if (form.delegator_id === form.delegate_id) {
    modalError.value = "Delegator and delegate must be different.";
    return;
  }
  saving.value = true;
  modalError.value = "";
  const token = store.state.auth.token;
  try {
    await createDelegation(token, {
      delegator_id: form.delegator_id,
      delegate_id: form.delegate_id,
    });
    $toast.open({ message: "Delegation created", type: "success", duration: 2000 });
    modalOpen.value = false;
    await load();
  } catch (error) {
    modalError.value = (error.response && error.response.data && JSON.stringify(error.response.data))
      || error.message || "Failed";
  }
  saving.value = false;
}

async function confirmDeactivate(d) {
  if (!window.confirm(
    `Deactivate delegation: ${fullName(d.delegate)} submitting for ${fullName(d.delegator)}?\n\n`
    + `Existing records they've already created are unaffected; they just won't be able to create/act on new drafts for this delegator.`
  )) return;
  const token = store.state.auth.token;
  try {
    await deactivateDelegation(token, d.id);
    $toast.open({ message: "Delegation deactivated", type: "success", duration: 2000 });
    await load();
  } catch (error) {
    $toast.open({ message: "Failed to deactivate", type: "error", duration: 3000 });
  }
}

onMounted(load);
</script>
